import type { Validator } from 'prop-types';
import PropTypes from 'prop-types';
import type { ReactNode } from 'react';
import { useState, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { lockPageScrollForModal, unlockPageScrollForModal } from './modalScrollLock.utils';

interface IReactPortal {
  children: React.ReactNode;
  wrapperId?: string;
  disableScrollLock?: boolean;
}

const createWrapperAndAppendToBody = (wrapperId: string) => {
  const wrapperElement = document.createElement('div');
  wrapperElement.setAttribute('id', wrapperId);
  document.body.appendChild(wrapperElement);

  return wrapperElement;
};

const ReactPortal: React.FC<IReactPortal> = ({
  children,
  wrapperId = 'react-portal-wrapper',
  disableScrollLock = false,
}) => {
  const [wrapperElement, setWrapperElement] = useState(document.getElementById(wrapperId));

  useLayoutEffect(() => {
    let element = document.getElementById(wrapperId);
    let systemCreated = false;

    if (!element) {
      systemCreated = true;
      element = createWrapperAndAppendToBody(wrapperId);
    }

    if (!disableScrollLock) {
      lockPageScrollForModal();
    }
    setWrapperElement(element);

    return () => {
      if (!disableScrollLock) {
        unlockPageScrollForModal();
      }

      if (systemCreated && element?.parentNode) {
        element.parentNode.removeChild(element);
      }
    };
  }, [wrapperId, disableScrollLock]);

  if (wrapperElement === null) return null;

  return createPortal(children, wrapperElement);
};

ReactPortal.propTypes = {
  children: PropTypes.node.isRequired as Validator<ReactNode>,
  wrapperId: PropTypes.string,
};

export default ReactPortal;
